import React from 'react';

interface QuickStartGuideProps {
  onClose: () => void;
}

export const QuickStartGuide: React.FC<QuickStartGuideProps> = ({ onClose }) => {
  const steps = [
    {
      title: 'Refine the results',
      description: 'Use the filters at the top (search, sector, or career cluster) to narrow in on occupations that match your interests.'
    },
    {
      title: 'Open a job card',
      description: 'Click any job card to view salary, skills, growth, and local context. The header buttons unlock deeper insights.'
    },
    {
      title: 'See real postings',
      description: 'Select “Job Postings” to check unique, recent listings for the role and learn which employers are actively hiring.'
    },
    {
      title: 'Explore employers',
      description: 'Choose “View Businesses” to see local companies that employ this occupation, including addresses and hiring scale.'
    },
    {
      title: 'Understand industries',
      description: 'Click “View Industries” for a breakout of every industry that hires this occupation, with employment totals and growth.'
    },
    {
      title: 'Plan the training',
      description: 'Use the education section plus the “Financial Aid” hub to identify credentials, scholarships, and grants that fit your path.'
    },
    {
      title: 'Chat or ask for help',
      description: 'The AI Career Advisor answers natural-language questions. You can also use Contact/Feedback in the footer to reach our team.'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4">
      <div className="bg-[#ffffff] rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[#e6e6e6] bg-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#1b2c5a] rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-[#f2c94c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4m5 3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1b2c5a] font-['Anton'] tracking-wide">Career Pathways Quick Start</h1>
              <p className="text-sm text-[#707070] font-['Roboto']">Seven actions to get value in the first five minutes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
            title="Close Quick Start Guide"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] bg-[#f8f9fa]">
          <div className="grid gap-4">
            {steps.map(({ title, description }, index) => (
              <div key={title} className="bg-white rounded-2xl border border-[#e6e6e6] shadow-sm p-5 flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1b2c5a] text-white flex items-center justify-center font-['Anton'] text-lg">
                  {index + 1}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#1b2c5a] font-['Roboto']">{title}</h2>
                  <p className="text-[#555] leading-relaxed font-['Roboto']">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-white border border-[#e6e6e6] rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-[#1b2c5a] font-['Roboto'] mb-2">Need a refresher later?</h3>
            <p className="text-[#555] leading-relaxed font-['Roboto']">
              Use “Restart Tour” in the footer to rerun the interactive walkthrough, or open this Quick Start Guide anytime for a fast overview.
              For deeper onboarding, contact your Forward Sumner team—they can pair this guide with your local workflows.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};



